import { useEffect, useState } from "react";
import type { HdriGroundConfig, Vec3 } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { CompactCheck, CompactNumber, CompactSlider, Row } from "../../inspector/rows";

/** HDRi grounded-dome projection (v18, full): worldRadius, tripodHeight,
 * originPosition (iJewel HDRiGroundPlugin). */
export function HdriGroundConfigControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<HdriGroundConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { r.getHdriGroundConfig().then(setC).catch((e: Error) => setError(e.message)); }, [r]);
  if (!c) return error ? <p className="px-3 py-1.5 text-[11px] text-ed-red">{error}</p> : null;
  const apply = async (next: HdriGroundConfig) => {
    setC(next); // optimistic: the dome rebuild below is a multi-step async op
    setBusy(true);
    try {
      await r.setHdriGround(next.enabled, { radius: next.worldRadius, tripodHeight: next.tripodHeight, originPosition: next.originPosition });
      setError(null);
    } catch (e) {
      // setHdriGround disposes and rebuilds a GroundedSkybox (dynamic import +
      // the equirect env texture) and genuinely rejects when no env is loaded.
      // Before, that rejection was an unhandled promise and the panel kept
      // showing the settings the user picked over a dome that never appeared.
      setError(e instanceof Error ? e.message : String(e));
    }
    // Re-read either way: on success the viewer may have clamped the values, on
    // failure the optimistic patch above must not survive as a lie.
    try {
      setC(await r.getHdriGroundConfig());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setBusy(false);
  };
  const setOrigin = (ax: keyof Vec3, v: number) => void apply({ ...c, originPosition: { ...c.originPosition, [ax]: v } });
  return (
    <div className="flex flex-col">
      <Row label="Enabled"><CompactCheck checked={c.enabled} onChange={(v) => void apply({ ...c, enabled: v })} /></Row>
      <Row label="World Radius"><CompactSlider value={c.worldRadius} min={1} max={1000} step={0.01} onChange={(v) => void apply({ ...c, worldRadius: v })} /></Row>
      <Row label="Tripod height"><CompactSlider value={c.tripodHeight} min={0} max={50} step={0.01} onChange={(v) => void apply({ ...c, tripodHeight: v })} /></Row>
      <Row label="Origin Position" stacked>
        <div className="flex items-center gap-1">
          {(["x", "y", "z"] as const).map((ax) => (
            <CompactNumber key={ax} value={c.originPosition[ax]} step={0.001} width={56} onChange={(v) => setOrigin(ax, v)} />
          ))}
        </div>
      </Row>
      {busy ? <span className="px-3 pb-1.5 text-[11px] text-ed-text-3">Rebuilding dome…</span> : null}
      {error ? <span className="px-3 pb-1.5 text-[11px] text-ed-red">{error}</span> : null}
    </div>
  );
}
